import { useMemo } from "react";
import { highlightInlineHtml } from "@/lib/topicSearch";

function highlightInline(text, terms) {
  return highlightInlineHtml(text, terms);
}

/**
 * Render EditorJS JSON content (or legacy HTML) to HTML inside .prose-doc container.
 */
function escapeHtml(str = "") {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function renderBlock(block, highlightTerms) {
  const { type, data = {} } = block || {};
  const hi = (text) => (highlightTerms?.length ? highlightInline(text, highlightTerms) : (text || ""));
  switch (type) {
    case "header": {
      const lvl = Math.min(6, Math.max(1, Number(data.level) || 2));
      return `<h${lvl}>${hi(data.text)}</h${lvl}>`;
    }
    case "paragraph":
      return `<p>${hi(data.text)}</p>`;
    case "list": {
      const tag = data.style === "ordered" ? "ol" : "ul";
      const items = (data.items || []).map((it) => {
        if (typeof it === "string") return `<li>${hi(it)}</li>`;
        if (it && typeof it === "object") return `<li>${hi(it.content || "")}</li>`;
        return "";
      }).join("");
      return `<${tag}>${items}</${tag}>`;
    }
    case "checklist": {
      const items = (data.items || []).map((it) =>
        `<li style="list-style:none;"><input type="checkbox" disabled ${it.checked ? "checked" : ""}/> ${hi(it.text || "")}</li>`
      ).join("");
      return `<ul style="padding-left:0;">${items}</ul>`;
    }
    case "quote":
      return `<blockquote><p>${hi(data.text)}</p>${data.caption ? `<cite>— ${hi(data.caption)}</cite>` : ""}</blockquote>`;
    case "code":
      return `<pre><code>${escapeHtml(data.code || "")}</code></pre>`;
    case "delimiter":
      return `<hr/>`;
    case "image": {
      const src = data.file?.url || data.url || "";
      const cap = data.caption ? `<figcaption style="text-align:center;font-size:0.85em;color:#888;margin-top:0.25rem;">${data.caption}</figcaption>` : "";
      const cls = [
        data.withBorder ? "ej-img-border" : "",
        data.stretched ? "ej-img-stretched" : "",
        data.withBackground ? "ej-img-bg" : "",
      ].filter(Boolean).join(" ");
      return `<figure><img src="${src}" alt="${escapeHtml(data.caption || "")}" class="${cls}"/>${cap}</figure>`;
    }
    case "embed": {
      const src = data.embed || data.source || "";
      if (!src) return "";
      const w = data.width || 640;
      const h = data.height || 360;
      return `<div class="ej-embed" style="position:relative;padding-bottom:56.25%;height:0;overflow:hidden;margin:1rem 0;">
        <iframe src="${src}" frameborder="0" allowfullscreen
          style="position:absolute;top:0;left:0;width:100%;height:100%;border-radius:0.25rem;"></iframe>
      </div>`;
    }
    case "table": {
      const rows = data.content || [];
      const withHead = data.withHeadings;
      const trs = rows.map((row, idx) => {
        const cells = row.map((c) => (withHead && idx === 0) ? `<th>${c}</th>` : `<td>${c}</td>`).join("");
        return `<tr>${cells}</tr>`;
      }).join("");
      return `<table>${trs}</table>`;
    }
    case "linkTool": {
      const meta = data.meta || {};
      const link = data.link || "#";
      return `<a href="${link}" target="_blank" rel="noopener noreferrer" class="ej-link-card" style="display:block;border:1px solid #e5e7eb;padding:0.75rem 1rem;border-radius:0.5rem;margin:1rem 0;text-decoration:none;color:inherit;">
        <div style="font-weight:600;">${meta.title || link}</div>
        ${meta.description ? `<div style="font-size:0.85em;opacity:0.75;margin-top:0.25rem;">${meta.description}</div>` : ""}
        <div style="font-size:0.75em;opacity:0.55;margin-top:0.25rem;">${link}</div>
      </a>`;
    }
    case "raw":
      return data.html || "";
    default:
      return "";
  }
}

export function editorJsToHtml(content, highlightTerms) {
  if (!content) return "";
  let data = content;
  if (typeof content === "string") {
    const trimmed = content.trim();
    if (!trimmed.startsWith("{")) {
      return highlightTerms?.length ? highlightInline(trimmed, highlightTerms) : content;
    }
    try {
      data = JSON.parse(trimmed);
    } catch {
      return content;
    }
  }
  if (!data || !Array.isArray(data.blocks)) return "";
  return data.blocks.map((b) => renderBlock(b, highlightTerms)).join("\n");
}

export default function EditorJsRenderer({ content, className = "prose-doc", testid, highlightTerms }) {
  const html = useMemo(() => editorJsToHtml(content, highlightTerms), [content, highlightTerms]);
  return (
    <div className={className} data-testid={testid} dangerouslySetInnerHTML={{ __html: html }} />
  );
}
